import React from 'react';
import HomeLayout from '../../hocs/layout/HomeLayout';
import { Meta } from '../../components/navigation/Helmet';

function PrivacyPolicy() {
  return (
    <HomeLayout>
      <Meta title="Política de Privacidad" />

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
              Política de Privacidad
            </h1>

            <div className="prose prose-lg max-w-none">
              <p className="text-gray-600 mb-6 text-center">
                <strong>Última actualización:</strong> 18 de julio de 2025
              </p>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">1. Introducción</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  En <strong>TableChat</strong>, desarrollada por Ovalcampus, respetamos su privacidad y
                  nos comprometemos a proteger su información personal. Esta política explica cómo
                  recopilamos, utilizamos, almacenamos, compartimos y protegemos sus datos cuando utiliza
                  nuestra plataforma para pedir y pagar en restaurantes sin esperar al mesero.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Operamos inicialmente en Colombia y cumplimos con la <strong>Ley 1581 de 2012</strong> y
                  el régimen de <strong>Habeas Data</strong>, así como con normativas internacionales
                  aplicables como <strong>GDPR</strong> y <strong>CCPA</strong>, en la medida correspondiente.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">2. Información que Recopilamos</h2>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">2.1 Datos de cuenta y perfil</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Nombre, apellidos y documento opcional para facturación.</li>
                  <li>Correo electrónico y número de teléfono.</li>
                  <li>Preferencias alimentarias y alergias (opcional).</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">2.2 Datos transaccionales</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Pedidos realizados, notas de platos, mesa/área, propina.</li>
                  <li>Método de pago y estado (aprobado/declinado). <em>No</em> almacenamos datos completos de tarjetas; el procesamiento lo realiza un proveedor certificado (p. ej., Stripe).</li>
                  <li>Historial de cuentas/recibos y reembolsos cuando aplique.</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">2.3 Datos técnicos y de uso</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Dirección IP, tipo de dispositivo/navegador, sistema operativo.</li>
                  <li>Cookies y tecnologías similares (ver sección 5).</li>
                  <li>Métricas de rendimiento, errores y eventos para mejorar el servicio.</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">2.4 Ubicación</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Ubicación aproximada para mostrar restaurantes cercanos (con su consentimiento).</li>
                  <li>Restaurante y número de mesa/área para enrutar su pedido correctamente.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">3. Cómo Usamos su Información</h2>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">3.1 Propósitos principales</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Procesar pedidos y pagos de forma segura.</li>
                  <li>Facilitar la comunicación en tiempo real con el restaurante.</li>
                  <li>Mostrar estados del pedido y tiempos estimados.</li>
                  <li>Brindar soporte y resolver incidencias.</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">3.2 Propósitos adicionales</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Personalizar recomendaciones (combos, sugerencias).</li>
                  <li>Analítica para mejorar menús y operación (datos agregados/seudonimizados cuando sea posible).</li>
                  <li>Prevención de fraude y cumplimiento legal.</li>
                  <li>Comunicaciones promocionales <strong>opt-in</strong> (puede retirarlas en cualquier momento).</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">4. Compartir Información</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  No vendemos su información. Compartimos datos solo en los casos siguientes:
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">4.1 Restaurantes socios</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Datos estrictamente necesarios para preparar y entregar su pedido.</li>
                  <li>Notas y alergias relevantes para la preparación.</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">4.2 Proveedores de servicios</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Procesadores de pago certificados (p. ej., Stripe).</li>
                  <li>Hosting y nube (p. ej., AWS), analítica y soporte técnico.</li>
                  <li>Mensajería y notificaciones (p. ej., email, push).</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">4.3 Requerimientos legales</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Órdenes judiciales o autoridades competentes.</li>
                  <li>Protección de derechos, prevención de fraude o incidentes de seguridad.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">5. Cookies y Tecnologías Similares</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Utilizamos cookies esenciales para el funcionamiento, de rendimiento para analizar uso, y de preferencia para recordar elecciones.
                  Las cookies de marketing se usan solo con su consentimiento. Puede gestionar sus preferencias en el navegador y, cuando aplique, en nuestro banner de consentimiento (GDPR/ePrivacy).
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">6. Seguridad de Datos</h2>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Cifrado TLS en tránsito; proveedores con certificaciones reconocidas.</li>
                  <li>Controles de acceso y registros de auditoría.</li>
                  <li>Buenas prácticas de almacenamiento (p. ej., no guardamos PAN completo de tarjetas).</li>
                  <li>Monitoreo y pruebas periódicas. Ningún sistema es 100% infalible.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">7. Sus Derechos</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Puede solicitar acceso, actualización, rectificación y supresión de sus datos; oponerse o limitar el tratamiento; y solicitar portabilidad.
                  En Colombia, puede presentar consultas y reclamos conforme a la Ley 1581 de 2012.
                </p>
                <p className="text-gray-700">
                  Escríbanos a <a className="text-blue-600 underline" href="mailto:privacy@tablechat.com">privacy@tablechat.com</a>. Responderemos en hasta 30 días, o según exija la regulación aplicable.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">8. Retención de Datos</h2>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Cuenta activa: mientras mantenga su cuenta.</li>
                  <li>Historial de pedidos y facturación: hasta 7 años por obligaciones contables/legales.</li>
                  <li>Registros técnicos: hasta 12 meses.</li>
                  <li>Marketing: hasta que retire su consentimiento.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">9. Transferencias Internacionales</h2>
                <p className="text-gray-700 leading-relaxed">
                  Sus datos pueden transferirse a otros países (p. ej., proveedores en EE. UU.). Implementamos salvaguardas apropiadas (cláusulas contractuales estándar u otras medidas reconocidas) para garantizar un nivel adecuado de protección.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">10. Menores de Edad</h2>
                <p className="text-gray-700 leading-relaxed">
                  TableChat no está dirigida a menores de 18 años. Si detectamos datos de menores, los eliminaremos y cerraremos la cuenta asociada.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">11. Cambios a esta Política</h2>
                <p className="text-gray-700 leading-relaxed">
                  Podemos actualizar esta política. Le notificaremos cambios significativos en la app o por correo. Revise la “Última actualización” al inicio.
                </p>
              </section>

              <section className="mb-2">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">12. Contacto / DPO</h2>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700"><strong>Email:</strong> privacy@tablechat.com</p>
                  <p className="text-gray-700"><strong>Dirección (Ovalcampus USA):</strong><br/>
                    1129 N Saint Lucas St<br/>
                    Allentown, PA 18104, Estados Unidos
                  </p>
                  <p className="text-gray-700"><strong>Horario:</strong> L–V 9:00–18:00 (hora local)</p>
                </div>
              </section>

              <div className="border-t border-gray-200 pt-8 mt-8 text-center">
                <p className="text-gray-600">
                  Al utilizar TableChat, confirma que ha leído y acepta esta Política de Privacidad.
                </p>
                <p className="text-gray-600 mt-4">
                  Para más información sobre términos de servicio, consulte: <a href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Términos de Servicio de Apple</a>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </HomeLayout>
  );
}

export default PrivacyPolicy;
